import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Entity extensions — user-defined metadata columns attached to an existing
 * entity, provisioned as a physical table in the app DB's `schema_ext` schema.
 *
 * Every procedure here runs DDL or reports on it, so they are administrative
 * actions rather than data entry.
 */

const UUIDSchema = z.string().uuid();

const SchemaExtensionSchema = z.object({
  id: UUIDSchema,
  targetModel: z.string(),
  schemaDefinitionId: UUIDSchema,
  /** Table name within the `schema_ext` schema. */
  tableName: z.string(),
  /** The version snapshot the provisioned columns currently reflect. */
  provisionedVersionId: UUIDSchema,
});

const ColumnChangeSchema = z.object({
  kind: z.enum(["add", "rename", "retype", "drop"]),
  fieldId: z.string(),
  /** Present on rename: the current column name. */
  from: z.string().optional(),
  /** Present on rename: the name it becomes. */
  to: z.string().optional(),
  /** Present on retype/drop: the column the change targets. */
  columnName: z.string().optional(),
});

const StatusResponseSchema = z.object({
  provisioned: z.boolean(),
  /** Which entity it extends, when provisioned. */
  targetModel: z.string().nullable(),
  tableName: z.string().nullable(),
  tableExists: z.boolean(),
  provisionedVersionId: UUIDSchema.nullable(),
  activeVersionId: UUIDSchema.nullable(),
  /** What a sync would apply, in the order it would apply it. */
  pendingChanges: z.array(ColumnChangeSchema),
  /** Edits a sync refuses because they would lose data. */
  rejectedChanges: z.array(
    z.object({ fieldName: z.string(), reason: z.string() }),
  ),
  /** Columns the provisioned snapshot expects that the table doesn't have. */
  missingColumns: z.array(z.string()),
  /**
   * Table-vs-schema drift, rendered by the UI. `syncable` marks the ones
   * `sync` can repair.
   */
  issues: z.array(
    z.object({
      severity: z.enum(["error", "warning"]),
      code: z.string(),
      message: z.string(),
      syncable: z.boolean(),
    }),
  ),
});

/**
 * The minimum a field needs for socket generation: `socketTypeForField` reads the
 * type and `isMultiple`, and the label names the socket.
 */
const SocketFieldSchema = z.object({
  id: z.string(),
  fieldName: z.string(),
  fieldType: z.string(),
  label: z.string(),
  isRequired: z.boolean(),
  isMultiple: z.boolean(),
  sortOrder: z.number(),
  description: z.string().nullable().optional(),
});

export const schemaExtensionsContracts = {
  /**
   * Socket sources for every provisioned extension of an entity: the table (which
   * namespaces the socket ids), the schema name (which labels them) and the
   * fields of the version the table currently reflects.
   *
   * The editor draws its `ext:` sockets from this and the workflow save path
   * canonicalizes from the same data, so the canvas and the server can't disagree
   * about what a node's sockets are.
   */
  listSocketSources: oc
    .input(z.object({ targetModel: z.string() }))
    .output(
      z.array(
        z.object({
          targetModel: z.string(),
          schemaDefinitionId: UUIDSchema,
          extensionName: z.string(),
          fields: z.array(SocketFieldSchema),
        }),
      ),
    )
    .route({ method: "GET" }),

  list: oc
    .input(z.object({ targetModel: z.string() }))
    .output(z.array(SchemaExtensionSchema))
    .route({ method: "GET" }),

  status: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(StatusResponseSchema)
    .route({ method: "GET" }),

  /**
   * What publishing the schema's current draft would do to its extension table.
   * Drives the destructive-change confirmation prompt: `destructive` non-empty
   * means `schemaDefinitions.publishVersion` will demand
   * `extensionSyncConfirmation` equal to `extensionName`.
   */
  previewPublishSync: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(
      z.object({
        registered: z.boolean(),
        extensionName: z.string().nullable(),
        tableName: z.string().nullable(),
        changes: z.array(ColumnChangeSchema),
        destructive: z.array(ColumnChangeSchema),
        rejected: z.array(
          z.object({ fieldName: z.string(), reason: z.string() }),
        ),
      }),
    )
    .route({ method: "GET" }),

  /**
   * Link an existing `sql` schema to an extendable entity and provision its
   * table, from the schema's active version. The only way a schema becomes an
   * extension after creation — `schemaDefinitions.update` can't touch
   * `targetModel`.
   */
  register: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        targetModel: z.string(),
      }),
    )
    .output(SchemaExtensionSchema)
    .route({ method: "POST" }),

  /**
   * Unlink a schema: drops the extension table and everything in it, so
   * `confirmation` must equal the schema's name.
   */
  unregister: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        confirmation: z.string(),
      }),
    )
    .output(z.object({ unregistered: z.boolean() }))
    .route({ method: "POST" }),

  provision: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(SchemaExtensionSchema)
    .route({ method: "POST" }),

  sync: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(
      z.object({
        tableName: z.string(),
        changes: z.array(ColumnChangeSchema),
        createdIndexes: z.array(z.string()),
        droppedIndexes: z.array(z.string()),
      }),
    )
    .route({ method: "POST" }),

  /** Drops the table and its data. The confirm belongs in the calling UI. */
  deprovision: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(z.object({ deprovisioned: z.boolean() }))
    .route({ method: "POST" }),
} as const;

export type SchemaExtensionResponse = z.infer<typeof SchemaExtensionSchema>;
export type SchemaExtensionStatusResponse = z.infer<
  typeof StatusResponseSchema
>;
